<template>
  <Layout>
    <PageHeader :title="pageTitle" :items="items" />
    <loading
      :active.sync="loading"
      color="#556ee6"
      loader="dots"
      :can-cancel="false"
      :is-full-page="false">
    </loading>

    <!-- Info Básica -->
    <div class="row">
      <div class="col-xl-12">
        <div class="card">
          <div class="card-body">
            <h5 class="card-title mb-3">Informações Básicas</h5>
            <div class="row">
              <div class="col-sm-12">
                <div class="form-group">
                  <label for="name">Nome da regra *</label>
                  <input
                    class="form-control"
                    type="text"
                    v-model="form.name"
                    :class="{ 'is-invalid': submitted && !form.name }"
                    placeholder="Ex: Envio de aéreo após confirmação"
                  >
                  <div v-if="submitted && !form.name" class="invalid-feedback">O campo é obrigatório.</div>
                </div>
              </div>
            </div>
            <div class="row mt-3">
              <div class="col-sm-12">
                <div class="form-group">
                  <label for="description">Descrição</label>
                  <textarea class="form-control" v-model="form.description" rows="2" placeholder="Descrição opcional da regra"></textarea>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Formulário vinculado -->
    <div class="row">
      <div class="col-xl-12">
        <div class="card">
          <div class="card-body">
            <h5 class="card-title mb-3">Formulário vinculado *</h5>
            <div class="row">
              <div class="col-sm-6">
                <div class="form-group">
                  <select
                    class="custom-select"
                    v-model="form.form_id"
                    :class="{ 'is-invalid': submitted && !form.form_id }"
                  >
                    <option value="">Selecione um formulário...</option>
                    <option v-for="f in forms" :key="f.id" :value="f.id">{{ f.name }}</option>
                  </select>
                  <div v-if="submitted && !form.form_id" class="invalid-feedback">Selecione um formulário.</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Condition Builder -->
    <div class="row">
      <div class="col-xl-12">
        <div class="card">
          <div class="card-body">
            <h5 class="card-title mb-3">Condições de disparo *</h5>
            <p class="text-muted mb-3">Defina quando esta regra deve ser aplicada a um participante.</p>
            <condition-builder
              v-model="conditionRules"
              :matchType.sync="conditionMatch"
              @update:matchType="conditionMatch = $event"
            />
            <div v-if="submitted && (!conditionRules || conditionRules.length === 0)" class="text-danger mt-2">
              <small>Adicione ao menos uma condição.</small>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Configuração de Transição -->
    <div class="row">
      <div class="col-xl-12">
        <div class="card">
          <div class="card-body">
            <h5 class="card-title mb-3">Transição de Status</h5>
            <div class="row mb-3">
              <div class="col-sm-6">
                <label>Ao enviar o formulário *</label>
                <div class="mt-2">
                  <div class="form-check form-check-inline">
                    <input class="form-check-input" type="radio" v-model="form.transition_type" value="auto" id="t_auto">
                    <label class="form-check-label" for="t_auto">Alterar status do participante</label>
                  </div>
                  <div class="form-check form-check-inline">
                    <input class="form-check-input" type="radio" v-model="form.transition_type" value="none" id="t_none">
                    <label class="form-check-label" for="t_none">Apenas coletar dados</label>
                  </div>
                </div>
              </div>
            </div>
            <div class="row" v-if="form.transition_type !== 'none'">
              <div class="col-sm-4">
                <div class="form-group">
                  <label>Status de destino</label>
                  <select class="custom-select" v-model="form.target_status_id" @change="onTargetStatusChange">
                    <option :value="null">Nenhum</option>
                    <option v-for="s in statusList" :key="s.id" :value="s.id">{{ s.name }}</option>
                  </select>
                  <small class="text-muted">O participante será movido para este status ao enviar o formulário.</small>
                </div>
              </div>
              <div class="col-sm-4" v-if="isTargetPendente && Object.keys(refStatuses).length > 0">
                <div class="form-group">
                  <label>Motivo da pendência</label>
                  <select class="custom-select" v-model="form.target_ref_status">
                    <option value="">Nenhum</option>
                    <option v-for="(name, id) in refStatuses" :key="id" :value="id">{{ name }}</option>
                  </select>
                  <small class="text-muted">Sub-status de pendência que será atribuído ao participante.</small>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Configuração de Email -->
    <div class="row">
      <div class="col-xl-12">
        <div class="card">
          <div class="card-body">
            <h5 class="card-title mb-3">Configuração de E-mail</h5>
            <div class="row">
              <div class="col-sm-6">
                <div class="form-group">
                  <label>Template de e-mail</label>
                  <select class="custom-select" v-model="form.email_template_id">
                    <option :value="null">Nenhum (não envia e-mail)</option>
                    <option v-for="tmpl in emailTemplates" :key="tmpl.id" :value="tmpl.id">
                      {{ tmpl.subject || `Template #${tmpl.id}` }}
                    </option>
                  </select>
                </div>
              </div>
              <div class="col-sm-3">
                <div class="form-group">
                  <label>Expiração do link (horas)</label>
                  <input class="form-control" type="number" v-model.number="form.token_expiration_hours" min="1">
                </div>
              </div>
            </div>
            <div class="alert alert-info mt-3" v-if="form.email_template_id">
              <strong>Variáveis disponíveis no template de e-mail:</strong>
              <ul class="mb-0 mt-2">
                <li><code>{$nome}</code> - Nome do participante</li>
                <li><code>{$email}</code> - E-mail do participante</li>
                <li><code>{$phone}</code> - Telefone do participante</li>
                <li><code>{$nomedoevento}</code> - Nome do evento</li>
                <li><code>{$urlacao}</code> - <strong>URL do magic link</strong> (coloque no botão/link do template)</li>
                <li><code>{$dataexpiracao}</code> - Data de expiração do link</li>
                <li><code>{$nomeregra}</code> - Nome da regra de follow-up</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Configuração de WhatsApp (só quando o evento tem WhatsApp ativo) -->
    <div class="row" v-if="waActive">
      <div class="col-xl-12">
        <div class="card">
          <div class="card-body">
            <h5 class="card-title mb-3">Configuração de WhatsApp</h5>
            <div class="row">
              <div class="col-sm-6">
                <div class="form-group">
                  <label>Template de WhatsApp</label>
                  <select class="custom-select" v-model="form.wa_template_id">
                    <option :value="null">Nenhum (não envia WhatsApp)</option>
                    <option v-for="tmpl in waTemplates" :key="tmpl.id" :value="tmpl.id">
                      {{ tmpl.name || `Template #${tmpl.id}` }}
                    </option>
                  </select>
                </div>
              </div>
            </div>
            <div class="alert alert-info mt-3" v-if="form.wa_template_id">
              O template será disparado quando o participante cair nesta regra. Se o template tiver botão de
              <strong>ação de retorno</strong> (URL dinâmica), o link individual
              (<code>?acao-retorno={token}</code>) é preenchido automaticamente com o token do participante.
              Apenas templates <strong>aprovados</strong> pela Meta são listados.
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Página de Obrigado -->
    <div class="row">
      <div class="col-xl-12">
        <div class="card">
          <div class="card-body">
            <h5 class="card-title mb-3">Página de Obrigado</h5>
            <p class="text-muted mb-3">Selecione a página de obrigado que será exibida após o participante enviar o formulário.</p>
            <div class="row">
              <div class="col-sm-6">
                <div class="form-group">
                  <label>Página de Obrigado</label>
                  <select class="custom-select" v-model="form.thank_you_page_id">
                    <option :value="null">Padrão do sistema</option>
                    <option v-for="page in thankYouPages" :key="page.id" :value="page.id">
                      {{ page.name }}{{ page.is_default ? ' (Padrão)' : '' }}
                    </option>
                  </select>
                  <small class="text-muted">Se nenhuma for selecionada, será usada a página padrão do sistema.</small>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Botões -->
    <div class="row mb-5">
      <div class="col-12 d-flex justify-content-end">
        <button class="btn btn-secondary me-2" @click="$router.push('/settings/follow-up-rules')">Cancelar</button>
        <button class="btn btn-primary" @click="saveItem" :disabled="saving">
          <span v-if="saving"><i class="bx bx-loader bx-spin mr-1"></i></span>
          Salvar
        </button>
      </div>
    </div>
  </Layout>
</template>

<script>
import Loading from 'vue-loading-overlay'
import 'vue-loading-overlay/dist/vue-loading.css'
import { mapActions, mapState } from 'vuex'
import { errorMessage, successMessage } from '@/components/alertsMessages'
import Layout from '@/components/layout/vertical'
import PageHeader from '@/components/page-header'
import ConditionBuilder from '@/components/ConditionBuilder'
import services from './services'

export default {
  components: {
    Loading,
    Layout,
    PageHeader,
    ConditionBuilder
  },
  data () {
    return {
      pageTitle: 'Nova Regra de Ação de Retorno',
      items: [
        { text: 'Configurações', href: '/' },
        { text: 'Ações de Retorno', href: '/settings/follow-up-rules' },
        { text: 'Nova Regra', active: true }
      ],
      loading: false,
      submitted: false,
      saving: false,
      form: {
        name: '',
        description: '',
        form_id: '',
        transition_type: 'auto',
        target_status_id: null,
        target_ref_status: '',
        email_template_id: null,
        wa_template_id: null,
        token_expiration_hours: 72,
        notify_admin_on_completion: false,
        is_active: true,
        priority: 0,
        thank_you_page_id: null
      },
      conditionMatch: 'all',
      conditionRules: [],
      forms: [],
      statusList: [],
      refStatuses: {},
      emailTemplates: [],
      waTemplates: [],
      thankYouPages: []
    }
  },
  computed: {
    ...mapState('followUpRules', { data: 'followUpRule' }),
    ...mapState('wa', ['settingWA']),
    waActive () {
      return this.settingWA && parseInt(this.settingWA.whatsapp) === 1
    },
    isEdit () {
      return this.$route.params.id && this.$route.params.id !== 'new'
    },
    isTargetPendente () {
      return String(this.form.target_status_id) === '2'
    }
  },
  mounted () {
    if (this.isEdit) {
      this.pageTitle = 'Editar Regra de Ação de Retorno'
      this.items[2].text = 'Editar Regra'
    }
    this.loadReferenceData()
    // Carrega a config de WhatsApp p/ decidir se a seção de WhatsApp aparece
    if (!this.settingWA || Object.keys(this.settingWA).length === 0) {
      this.$store.dispatch('wa/GetSettingWA').catch(() => {})
    }
    if (this.isEdit) {
      this.loadItem(this.$route.params.id)
    }
  },
  methods: {
    ...mapActions('followUpRules', {
      getRule: 'show',
      postData: 'save'
    }),
    async loadReferenceData () {
      try {
        const [formsRes, emailsRes, statusRes, refStatusRes, thankYouRes] = await Promise.all([
          services.getForms({ type: 'action' }),
          services.getEmailTemplates(),
          services.getStatuses(),
          services.getRefStatuses(),
          services.getThankYouPages()
        ])
        this.forms = formsRes.data.data || formsRes.data || []
        this.emailTemplates = emailsRes.data.data || emailsRes.data || []
        this.statusList = statusRes.data || []
        this.refStatuses = refStatusRes.data || {}
        this.thankYouPages = thankYouRes.data.data || thankYouRes.data || []
      } catch (e) {
        console.error('Erro ao carregar dados de referência', e)
      }

      // Templates de WhatsApp em chamada isolada: se o endpoint falhar (WhatsApp
      // não configurado), não quebra o resto do formulário. Só os aprovados.
      try {
        const waRes = await services.getWaTemplates()
        const all = waRes.data.data || waRes.data || []
        this.waTemplates = all.filter(t => String(t.status).toUpperCase() === 'APPROVED')
      } catch (e) {
        this.waTemplates = []
      }
    },
    onTargetStatusChange () {
      this.form.target_ref_status = ''
    },
    async loadItem (id) {
      this.loading = true
      try {
        await this.getRule(id)
        this.form = { ...this.data }
        if (this.form.conditions) {
          this.conditionMatch = this.form.conditions.match || 'all'
          this.conditionRules = this.form.conditions.rules || []
        }
      } catch (e) {
        errorMessage('Erro ao carregar regra.')
      }
      this.loading = false
    },
    async saveItem () {
      this.submitted = true

      if (!this.form.name || !this.form.form_id || !this.conditionRules.length) {
        window.scroll(0, 0)
        return
      }

      this.saving = true

      const payload = {
        ...this.form,
        conditions: {
          match: this.conditionMatch,
          rules: this.conditionRules
        }
      }

      try {
        await this.postData(payload)
        successMessage('Regra salva com sucesso.', 2500)
        this.$router.push('/settings/follow-up-rules')
      } catch (err) {
        errorMessage('Erro ao salvar: ' + (err.response?.data?.message || err.message))
      }

      this.saving = false
    }
  }
}
</script>

<style scoped>
.vld-overlay.is-full-page {
  z-index: 1051 !important;
}
</style>
